/** @jsx jsx */

import React, { useState, useEffect } from 'react';

import { Button } from '../../navigation/Button';
import { Icon } from '../../display/Icon';
import { css, jsx } from '@emotion/react';
import { Animation, Shadows } from '../../../theme';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

type Props = {
  /**
   * Title, shown both when closed and open.
   */
  title: React.ReactNode;

  /**
   * Content, only shown when open.
   */
  children: React.ReactNode;

  /**
   * Optional link, visible as an icon next to/under (depends on viewport) the content when open.
   */
  href?: string;

  /**
   * Lets a parent open the `Accordion` on its initial render.
   */
  isInitiallyOpen?: boolean;

  /**
   * Enables `Accordion` to act as a semi-controlled component.
   * A parent will be able to manage the open state. Clicking/toggling
   * the component will still take precedence until this prop changes again.
   */
  isOpen?: boolean;

  /**
   * Callback when the `Accordion` is toggled.
   */
  onToggle?: ({ isOpen }: { isOpen: boolean }) => void;

  /**
   * Determines which side the `Accordion` toggle should be on.
   * @default 'left'
   */
  toggleSide?: 'left' | 'right';
};

type DerivedState = {
  isOpen: boolean;
  toggleSide: 'left' | 'right';
};

const Styles = labelStyles('Accordion', {
  root: (derivedState: DerivedState) => [
    css`
      position: relative;
      background-color: var(--zds-background-weaker);
      border: var(--zds-border-width-strong) solid transparent;
      border-radius: var(--zds-radius-medium);
      transition-property: box-shadow, border-color, background-color;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: ${Animation.transitionDuration};

      &:hover {
        background-color: var(--zds-background-selected);
      }
    `,
    derivedState.isOpen &&
      withModifier(
        'highlighted',
        css`
          background-color: var(--zds-background-default);
          box-shadow: ${Shadows.elevation5};
        `
      ),
  ],
  summary: (derivedState: DerivedState) => [
    css`
      display: block;
      padding: var(--zds-space-20);
      padding-${derivedState.toggleSide}: var(--zds-space-48);
      font: var(--zds-paragraph-header-3);
      color: var(--zds-text-default);
      cursor: pointer;

      // :focus-visible only works on the summary element,
      // so this expands the focus ring to the whole accordion.
      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border-radius: var(--zds-radius-medium);
      }

      &:focus-visible::before {
        border: var(--zds-border-focus);
        border-radius: var(--zds-radius-medium);
      }

      // Remove marker from browser / polyfills.
      &::-webkit-details-marker {
        display: none;
      }
    `,
  ],
  content: (derivedState: DerivedState) => css`
    position: relative;
    z-index: 1;
    padding: 0 var(--zds-space-20) var(--zds-space-20);
    padding-${derivedState.toggleSide}: var(--zds-space-48);
    font: var(--zds-body-1-multiline);
    color: var(--zds-text-stronger);

    p {
      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
`,
  arrow: (derivedState: DerivedState) => css`
    position: absolute;
    top: var(--zds-size-20);
    ${derivedState.toggleSide}: var(--zds-space-16);
  `,
  fullPageButton: css`
    margin: 0 var(--zds-space-20) var(--zds-space-20);
  `,
});

const FullPageButton = (props: Props) => {
  const iconName = 'arrowOffsite';

  return (
    <div css={Styles.fullPageButton} data-zds>
      <Button
        color="secondary"
        href={props.href}
        iconBefore={iconName}
        isFullWidth={true}
        size="small"
        target="_blank"
      >
        Open full page
      </Button>
    </div>
  );
};

/**
 * A styled `<details>` and `<summary>` component that opens and closes on click.
 */
export const Accordion = (props: Props) => {
  const {
    children,
    href,
    isInitiallyOpen,
    onToggle = () => {},
    title,
    toggleSide = 'left',
  } = props;

  const [isOpen, setIsOpen] = useState(!!isInitiallyOpen);

  const derivedState = {
    isOpen,
    toggleSide,
  };

  useEffect(() => {
    if (props.isOpen !== undefined) {
      setIsOpen(props.isOpen);
    }
  }, [props.isOpen]);

  const handleClick = (e: React.SyntheticEvent<EventTarget, Event>) => {
    e.preventDefault();
    onToggle({ isOpen: !isOpen });

    /**
     * We prevent internal state management to consider multiple controlled Accordions.
     * In this case, an Accordion might toggle states and then re-render based on
     * `isOpen`. With `useEffect`, there will be slight jank when the the effect
     * fires, because the browser has already re-painted.
     */
    if (props.isOpen === undefined) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <details css={Styles.root(derivedState)} open={isOpen} data-zds>
      <summary
        css={Styles.summary(derivedState)}
        onClick={handleClick}
        data-zds
      >
        <span css={Styles.arrow(derivedState)} data-zds>
          <Icon
            canAcceptPointerEvents={false}
            name={isOpen ? 'arrowSmallUp' : 'arrowSmallDown'}
            size={24}
          />
        </span>
        {title}
      </summary>

      <div css={Styles.content(derivedState)} data-zds>
        {children}
      </div>

      {isOpen && href && <FullPageButton {...props} />}
    </details>
  );
};
